import React, { useState, useEffect, useRef, useCallback } from 'react';
import { Box, Chip } from '@mui/material';

/**
 * Sticky category navigation with scroll auto-select (scrollspy).
 *
 * Owns the refs + IntersectionObserver wiring so both the customer dashboard
 * shop (NewOrderView) and the public store (PublicStorePage) share one
 * implementation. Each page renders its own product sections and wires them
 * up via the returned `setSectionRef` / `setChipRef` callbacks.
 *
 * @param {string[]} categories - ordered category labels
 * @returns refs + active category + scroll helper
 */
export function useCategoryScrollspy(categories) {
    const [selectedCategory, setSelectedCategory] = useState('');
    const sectionRefs    = useRef({}); // { cat: sectionEl }
    const chipRefs       = useRef({}); // { cat: chipEl }
    const stickyBarRef   = useRef(null);
    const observerRef    = useRef(null);
    const clickScrollRef = useRef(false); // suppress scrollspy during programmatic scroll
    const clickTimerRef  = useRef(null);
    const categoryKey = categories.join('|');

    // Default the active category to the first one once categories load
    useEffect(() => {
        if (categories.length > 0 && !categories.includes(selectedCategory)) {
            setSelectedCategory(categories[0]);
        }
    }, [categoryKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Scrollspy — one observer per category set. Sections attach/detach themselves
    // through `setSectionRef`, so it survives step navigation (browse ⇄ checkout)
    // that mounts/unmounts the sections while this hook stays alive.
    useEffect(() => {
        if (categories.length < 2) return undefined;
        const bar = stickyBarRef.current;
        const stickyTop = bar ? (parseFloat(getComputedStyle(bar).top) || 0) : 0;
        const barH = bar ? bar.offsetHeight : 52;
        const offset = Math.round(stickyTop + barH + 6);

        const observer = new IntersectionObserver((entries) => {
            if (clickScrollRef.current) return; // ignore while click-scrolling
            const visible = entries.filter(e => e.isIntersecting);
            if (visible.length === 0) return;
            const topmost = visible.reduce((a, b) =>
                a.boundingClientRect.top < b.boundingClientRect.top ? a : b);
            setSelectedCategory(topmost.target.dataset.cat);
        }, { rootMargin: `-${offset}px 0px -62% 0px`, threshold: 0 });

        observerRef.current = observer;
        // Pick up any sections already mounted
        Object.values(sectionRefs.current).forEach(el => el && observer.observe(el));
        return () => { observer.disconnect(); observerRef.current = null; };
    }, [categoryKey]); // eslint-disable-line react-hooks/exhaustive-deps

    // Keep the active chip scrolled into view within the horizontal bar
    useEffect(() => {
        const chip = chipRefs.current[selectedCategory];
        if (chip) chip.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });
    }, [selectedCategory]);

    const scrollToCategory = useCallback((cat) => {
        setSelectedCategory(cat);
        clickScrollRef.current = true;
        sectionRefs.current[cat]?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        clearTimeout(clickTimerRef.current);
        clickTimerRef.current = setTimeout(() => { clickScrollRef.current = false; }, 700);
    }, []);

    // Ref callback that observes the section on mount and unobserves on unmount
    const setSectionRef = useCallback((cat) => (el) => {
        const prev = sectionRefs.current[cat];
        if (prev && observerRef.current) observerRef.current.unobserve(prev);
        sectionRefs.current[cat] = el;
        if (el && observerRef.current) observerRef.current.observe(el);
    }, []);
    const setChipRef = useCallback((cat) => (el) => { chipRefs.current[cat] = el; }, []);

    return { selectedCategory, scrollToCategory, stickyBarRef, setSectionRef, setChipRef };
}

/**
 * Horizontal sticky bar of category chips. Renders nothing for < 2 categories.
 *
 * @param {string} top - CSS value for `position: sticky` top offset
 *   (e.g. 'calc(48px + env(safe-area-inset-top, 0px))' under a fixed app bar, or 0)
 * @param {object} sx - extra styles (e.g. negative-margin bleed to screen edges)
 */
export function StickyCategoryBar({ categories, selectedCategory, onSelect, stickyBarRef, setChipRef, top = 0, sx }) {
    if (categories.length < 2) return null;
    return (
        <Box
            ref={stickyBarRef}
            sx={{
                position: 'sticky',
                top,
                zIndex: 5,
                py: 1,
                mb: 1.5,
                bgcolor: 'background.paper',
                borderBottom: '1px solid',
                borderColor: 'divider',
                display: 'flex',
                gap: 1,
                overflowX: 'auto',
                flexWrap: 'nowrap',
                scrollbarWidth: 'none',
                '&::-webkit-scrollbar': { display: 'none' },
                ...sx,
            }}
        >
            {categories.map((cat) => (
                <Chip
                    key={cat}
                    ref={setChipRef(cat)}
                    label={cat}
                    size="small"
                    onClick={() => onSelect(cat)}
                    color={selectedCategory === cat ? 'primary' : 'default'}
                    variant={selectedCategory === cat ? 'filled' : 'outlined'}
                    sx={{ flexShrink: 0 }}
                />
            ))}
        </Box>
    );
}

/** Standard top offset for a section so it lands just below the sticky bar after a jump. */
export const sectionScrollMargin = (top) =>
    `calc(${top} + 60px)`;
